import React, { useEffect, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { Switch, Table } from 'tea-component';
import { useDispatch, useIntl } from 'umi';
import TableCommonHook from '@op/components/TableCommonHook';
import useCompareCompetitorsColumns from '@op/components/TableCommonHook/hooks/useCompareCompetitorsColumns';
import './index.scss';
import {
  useCompareCompetitorsKeyMetrics,
  useCompareCompetitorsKeyMetricsGrouping,
} from '../../SameStartDate/hooks';
import { formatCompareCompetitorsKeyMetrics } from '../../utils';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getMetricsItemFunc } from '@op/components/FilterCommon/widgets/MetricsSelect/config';
import { downloadAction } from '@op/pages/download/utils';
import _ from 'lodash-es';
import { NO_DATE } from '@op/constants';
import { SortBy } from 'tea-component/lib/table/addons';
import { DownloadProps } from '@op/pages/download/types';

const switches = [
  { id: 'region', label: 'Show Region' },
  { id: 'country', label: 'Show Country' },
  { id: 'language', label: 'Show Language' },
  { id: 'channel', label: 'Show Channel' },
];
const { radioable } = Table.addons;

const GameComparisonTable = ({
  metrics,
  gameData,
  filter,
  color,
  gameInfo,
  dateType,
  showDate,
  MergeChannelIconElement,
  changeGameInfo,
  selectedGameInfo,
  handleFilterValue
}: any) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [showChange, setShowChange] = useState(true);
  const [activeSwitch, setActiveSwitch] = useState('');
  const [realSwitch, setRealSwitch] = useState('');
  const getMetricsItem = useMemo(
    () => getMetricsItemFunc(gameInfo?.entity_type || 'mobile'),
    [gameInfo],
  );

  const filterParams = useMemo(
    () => ({
      ...filter,
      group_by: realSwitch,
    }),
    [realSwitch, filter],
  );

  const {
    data: keyMetricsData,
    loading,
    error,
  } = useCompareCompetitorsKeyMetrics({
    filter,
    formatData: formatCompareCompetitorsKeyMetrics,
  });

  const { loading: groupingLoading, state: keyMetricsGroupingData } =
    useCompareCompetitorsKeyMetricsGrouping({
      filter: filterParams,
    });

  const columnsParams = useMemo(
    () => ({
      ...filter,
      ...gameInfo,
    }),
    [filter, gameInfo],
  );
  const { columns, sortColumns } = useCompareCompetitorsColumns({
    columnsKey: metrics,
    filter: columnsParams,
    gameData,
    showChange,
    activeSwitch: realSwitch,
    color,
    keyMetricsGroupingData,
    getMetricsItem,
    dateType,
    showDate,
    MergeChannelIconElement,
  });

  const debouncedUpdate = useMemo(
    () =>
      _.debounce((id: string) => {
        setRealSwitch(id);
        if (id) {
          buriedBtnPoint('SplitMetrics', id);
        }
      }, 300),
    [],
  );

  const handleSwitchChange = (switchItemId: string, value: boolean) => {
    const id = value ? switchItemId : '';
    setActiveSwitch(id);
    debouncedUpdate(id);
  };

  const [sort, setSort] = useState<SortBy<any>>({
    order: 'desc',
    by: metrics[0],
  });

  useEffect(() => {
    setSort({ order: 'desc', by: metrics[0] });
  }, [metrics]);

  const records = useMemo(() => {
    if (!keyMetricsData || !Array.isArray(keyMetricsData)) return [];

    let data = keyMetricsData;
    let currentItem: any = null;
    data = data.filter((item) => {
      if (item.game_id === gameInfo?.edition_unified_id) {
        currentItem = item;
        return false;
      }
      return true;
    });

    const { by, order } = sort;
    const newList = data.sort((a: any, b: any) => {
      if (by === 'brand_health') {
        // 将"-"值排在最后
        const aVal = a[by];
        const bVal = b[by];

        if (aVal === '-' && bVal === '-') return 0;
        if (aVal === '-') return 1;
        if (bVal === '-') return -1;
      } else {
        // 将"-"值排在最后
        const aVal = a[by];
        const bVal = b[by];

        if (aVal === 0 && bVal === 0) return 0;
        if (aVal === 0) return 1;
        if (bVal === 0) return -1;
      }

      // 默认排序逻辑
      if (a[by] < b[by]) return order === 'asc' ? -1 : 1;
      if (a[by] > b[by]) return order === 'asc' ? 1 : -1;
      return 0;
    });

    if (currentItem) {
      newList.unshift(currentItem);
    }

    return newList;
  }, [keyMetricsData, sort]);

  function toggleExpand(visible = false) {
    dispatch({
      type: 'opinionMenu/toggleMenuExpand',
      payload: {
        menuExpand: visible,
      },
    });
  }

  return (
    <CardCommon
      className="dbo-game-comparison-table"
      loading={loading}
      loadingHeight={350}
      empty={error}
      emptyHeight={350}
      emptyTitleText={NO_DATE.ERROR}
      emptyDescText={''}
      logBtnId="Game Comparison Table"
      hasDownload
      handleDownloadOnClick={() => {
        const params: DownloadProps = {
          key: 'ComparisonGameTable',
          fileName: 'Game Comparison Table',
          filter: {
            ...filter,
            ...gameInfo,
          },
          auxiliaryParams: { gameData, dateType, data: keyMetricsData, groupBy: realSwitch },
        };
        if (realSwitch !== '') {
          params.key = 'ComparisonGameTableWithGroup';
          params.auxiliaryParams = { gameData, dateType, data: keyMetricsData, groupBy: realSwitch };
        } else {
          params.data = keyMetricsData;
        }
        downloadAction(params);
      }}
      title={formatMessage({ id: 'Game Comparison Table' })}
    >
      <div className="switch-group">
        <Switch value={showChange} onChange={(value) => setShowChange(value)}>
          {formatMessage({ id: 'ShowChange%-1' })}
        </Switch>
        {switches.map((switchItem) => (
          <Switch
            name={switchItem.id}
            value={activeSwitch === switchItem.id}
            onChange={(value) => {
              handleSwitchChange(switchItem.id, value);
            }}
            key={switchItem.id}
            className="unique-switch"
          >
            {formatMessage({ id: switchItem.label })}
          </Switch>
        ))}
      </div>
      <TableCommonHook
        recordKey={(cvm) => cvm.id}
        records={records}
        total={records?.length || 0}
        columns={columns as any}
        sortColumns={sortColumns}
        hasPageable={false}
        bordered="all"
        sortInfo={[sort, setSort]}
        addons={[
          radioable({
            value: selectedGameInfo?.edition_unified_id,
            onChange: (_key, { record }) => {
              const { game_id, start_time, end_time } = record;
              const value = gameData[game_id];
              if (value) {
                toggleExpand();
              }
              // buriedBtnPoint('OpenGameDetail', value);
              changeGameInfo(value);
              handleFilterValue?.({
                start_time,
                end_time
              });
            },
            rowSelect: true,
          }),
        ]}
      // rowClassName={(record) => {
      //   if (record?.edition_unified_id === gameInfo?.edition_unified_id) {
      //     return 'dbo-game-comparison-table-main-game';
      //   }
      //   return '';
      // }}
      ></TableCommonHook>
    </CardCommon>
  );
};

export default GameComparisonTable;
